import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Table } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './QuestionnaireAnalytics.css';

/**
 * Interface for patient with submissions from backend.
 */
interface PatientWithSubmissions {
  patient_id: string;
  latest_submission: string;
  total_submissions: number;
  questionnaires: string[];
}

/**
 * Interface for patient basic info.
 */
interface PatientInfo {
  patient_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  amka?: string;
  date_of_birth?: string;
  gender?: string;
  mobile_phone?: string;
  landline_phone?: string;
}

/**
 * Interface for questionnaire response.
 */
interface QuestionnaireResponse {
  questionnaire_id: string;
  patient_id: string;
  status: string;
  answers: { [key: string]: any };
  scores?: { [key: string]: any };
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  started_at?: string;
}

/**
 * Interface for patient questionnaire summary.
 */
interface PatientQuestionnaireSummary {
  patient_id: string;
  total_questionnaires: number;
  completed_questionnaires: number;
  in_progress_questionnaires: number;
  questionnaires: QuestionnaireResponse[];
}

/**
 * QuestionnaireAnalytics Component
 * 
 * Displays analytics for all patients with questionnaire submissions.
 * Shows patient info, scores, and completion time for each questionnaire.
 */
const QuestionnaireAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [patientsWithSubmissions, setPatientsWithSubmissions] = useState<PatientWithSubmissions[]>([]);
  const [patientInfoMap, setPatientInfoMap] = useState<{ [key: string]: PatientInfo }>({});
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [questionnaireSummary, setQuestionnaireSummary] = useState<PatientQuestionnaireSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all'); // Default to 'all' - always one selected
  const [selectedQuestionnaireTypeFilter, setSelectedQuestionnaireTypeFilter] = useState<string | null>(null); // 'hamilton', 'aq', etc.
  const [searchQuery, setSearchQuery] = useState<string>(''); // Search query for patient list
  const [mobileView, setMobileView] = useState<'list' | 'details'>('list'); // Mobile view toggle
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 992); // Detect mobile
  const [unreadCounts, setUnreadCounts] = useState<{ [patientId: string]: number }>({}); // Track unread count per patient
  const [patientStatusCounts, setPatientStatusCounts] = useState<{ [patientId: string]: { inProgress: number; completed: number } }>({}); // Track status counts per patient

  // Persisted unread tracking
  const LS_UNREAD_KEY = 'qa_last_seen_submission_counts';

  const loadLastSeenMap = (): { [patientId: string]: number } => {
    try {
      const raw = localStorage.getItem(LS_UNREAD_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const saveLastSeenMap = (map: { [patientId: string]: number }) => {
    try {
      localStorage.setItem(LS_UNREAD_KEY, JSON.stringify(map));
    } catch {
      // ignore write errors (e.g., storage full)
    }
  };

  /**
   * Fetch all patients with submissions (sorted by latest submission).
   */
  const fetchPatientsWithSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch('/api/patients/with-submissions?sort_order=desc&limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch patients: ${response.status}`);
      }

      const result = await response.json();
      const patients = result.data.patients || [];
      setPatientsWithSubmissions(patients);
      
      // Fetch patient info for all patients
      if (patients.length > 0) {
        await fetchAllPatientInfo(patients.map((p: PatientWithSubmissions) => p.patient_id));
        
        // Initialize unread counts from persisted "last seen" values
        const lastSeenMap = loadLastSeenMap();
        const initialUnreadCounts: { [patientId: string]: number } = {};
        patients.forEach((patient: PatientWithSubmissions) => {
          const lastSeen = lastSeenMap[patient.patient_id] ?? 0;
          initialUnreadCounts[patient.patient_id] = Math.max(0, patient.total_submissions - lastSeen);
        });
        setUnreadCounts(initialUnreadCounts);
        
        // Fetch status counts for all patients
        patients.forEach((patient: PatientWithSubmissions) => {
          fetchPatientStatusCounts(patient.patient_id);
        });
      }
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch patient info for multiple patients.
   */
  const fetchAllPatientInfo = async (patientIds: string[]) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const infoMap: { [key: string]: PatientInfo } = {};
    
    // Fetch all in parallel
    await Promise.all(
      patientIds.map(async (patientId) => {
        try {
          const response = await fetch(`/patients/${patientId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const result = await response.json();
            infoMap[patientId] = result.data;
          } else {
            // Fallback info
            infoMap[patientId] = {
              patient_id: patientId,
              first_name: 'Patient',
              last_name: patientId.substring(0, 8),
            };
          }
        } catch (err) {
          console.error(`Error fetching patient ${patientId}:`, err);
          infoMap[patientId] = {
            patient_id: patientId,
            first_name: 'Patient',
            last_name: patientId.substring(0, 8),
          };
        }
      })
    );

    setPatientInfoMap(infoMap);
  };

  /**
   * Fetch patient questionnaire summary.
   */
  const fetchQuestionnaireSummary = async (patientId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`/api/questionnaires/patient/${patientId}/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        setQuestionnaireSummary(result.data);
      } else {
        console.warn(`Failed to fetch questionnaire summary: ${response.status}`);
        setQuestionnaireSummary(null);
      }
    } catch (err) {
      console.error('Error fetching questionnaire summary:', err);
      setQuestionnaireSummary(null);
    }
  };

  /**
   * Fetch patient status counts (in_progress and completed questionnaires).
   */
  const fetchPatientStatusCounts = async (patientId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`/api/patients/${patientId}/questionnaires/stats-by-category`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        const stats = result.data.statistics;
        
        // Calculate total counts across all questionnaire types
        let totalInProgress = 0;
        let totalCompleted = 0;
        
        Object.values(stats).forEach((stat: any) => {
          totalInProgress += stat.inProgressCount || 0;
          totalCompleted += stat.completedCount || 0;
        });
        
        setPatientStatusCounts(prev => ({
          ...prev,
          [patientId]: {
            inProgress: totalInProgress,
            completed: totalCompleted
          }
        }));
      } else {
        console.warn(`Failed to fetch status counts for patient ${patientId}: ${response.status}`);
      }
    } catch (err) {
      console.error(`Error fetching status counts for patient ${patientId}:`, err);
    }
  };

  /**
   * Load questionnaire details when selected patient changes.
   */
  useEffect(() => {
    if (selectedPatientId) {
      setDetailLoading(true);
      setQuestionnaireSummary(null);
      
      // Refresh both questionnaire summary and status counts
      Promise.all([
        fetchQuestionnaireSummary(selectedPatientId),
        fetchPatientStatusCounts(selectedPatientId)
      ]).finally(() => {
        setDetailLoading(false);
      });
    }
  }, [selectedPatientId]);

  /**
   * Refresh questionnaire data when component becomes visible.
   * This ensures we get the latest data after GDPR consent changes.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedPatientId) {
        // Refresh data when page becomes visible
        fetchQuestionnaireSummary(selectedPatientId);
        fetchPatientStatusCounts(selectedPatientId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also refresh on focus
    const handleFocus = () => {
      if (selectedPatientId) {
        fetchQuestionnaireSummary(selectedPatientId);
        fetchPatientStatusCounts(selectedPatientId);
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedPatientId]);

  /**
   * Periodic refresh to catch GDPR consent changes.
   * This ensures we get the latest data even if GDPR consent happens in another tab.
   */
  useEffect(() => {
    if (!selectedPatientId) return;

    // Refresh every 10 seconds to catch GDPR consent changes
    const interval = setInterval(() => {
      fetchQuestionnaireSummary(selectedPatientId);
      fetchPatientStatusCounts(selectedPatientId);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [selectedPatientId]);

  /**
   * Load initial data on component mount.
   */
  useEffect(() => {
    fetchPatientsWithSubmissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Set default patient selection if no patient is selected and no URL params.
   */
  useEffect(() => {
    const patientId = searchParams.get('patient');
    
    // Only set default patient if no patient is selected and no URL params
    if (!selectedPatientId && !patientId && patientsWithSubmissions.length > 0) {
      setSelectedPatientId(patientsWithSubmissions[0].patient_id);
    }
  }, [patientsWithSubmissions, selectedPatientId, searchParams]);

  /**
   * Handle URL search parameters for deep linking with filters.
   * This allows navigation from the questionnaire assignment modal.
   */
  useEffect(() => {
    const patientId = searchParams.get('patient');
    const questionnaireType = searchParams.get('questionnaire');
    const status = searchParams.get('status');

    // Apply filters from URL parameters
    if (status && ['all', 'completed', 'in_progress'].includes(status)) {
      setSelectedStatusFilter(status);
    }

    if (questionnaireType && ['hamilton', 'aq', 'eq', 'zung', 'scid'].includes(questionnaireType)) {
      setSelectedQuestionnaireTypeFilter(questionnaireType);
    }

    // Select patient if specified
    if (patientId) {
      setSelectedPatientId(patientId);
    }

    // Don't clear URL parameters - keep them for bookmarking and refresh
    // The parameters will be preserved in the URL
  }, [searchParams, setSearchParams]);

  /**
   * Handle window resize for responsive mobile/desktop detection.
   */
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      // Reset to list view on mobile when resizing down
      if (mobile && mobileView === 'details' && !selectedPatientId) {
        setMobileView('list');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileView, selectedPatientId]);

  /**
   * Handle status filter click (All, Completed, In Progress).
   * One status filter is always selected - clicking the same filter does nothing.
   * Can be combined with questionnaire type filter.
   */
  const handleStatusFilterClick = (status: string) => {
    // Always keep one status filter selected
    if (selectedStatusFilter !== status) {
      setSelectedStatusFilter(status);
    }
    // If clicking the same filter, do nothing (keep it selected)
  };

  /**
   * Handle questionnaire type filter click (Hamilton, AQ, EQ, etc.).
   * Can be combined with status filter.
   */
  const handleQuestionnaireTypeFilterClick = (questionnaireId: string) => {
    if (selectedQuestionnaireTypeFilter === questionnaireId) {
      // Clicking same filter again - clear filter
      setSelectedQuestionnaireTypeFilter(null);
    } else {
      // Set new filter (keeps status filter active)
      setSelectedQuestionnaireTypeFilter(questionnaireId);
    }
  };

  /**
   * Get filtered questionnaires based on active filters.
   */
  const getFilteredQuestionnaires = (): QuestionnaireResponse[] => {
    if (!questionnaireSummary || !questionnaireSummary.questionnaires) {
      return [];
    }

    let filtered = questionnaireSummary.questionnaires.filter(q => q.status !== 'not_started');

    // Apply status filter
    if (selectedStatusFilter === 'completed') {
      filtered = filtered.filter(q => q.status === 'completed');
    } else if (selectedStatusFilter === 'in_progress') {
      filtered = filtered.filter(q => q.status === 'in_progress');
    }
    // 'all' shows everything (already filtered not_started)

    // Apply questionnaire type filter
    if (selectedQuestionnaireTypeFilter) {
      filtered = filtered.filter(q => q.questionnaire_id === selectedQuestionnaireTypeFilter);
    }

    return filtered;
  };

  /**
   * Get questionnaire display name.
   */
  const getQuestionnaireDisplayName = (questionnaireId: string): string => {
    const names: { [key: string]: string } = {
      'hamilton': 'Hamilton Anxiety Scale',
      'aq': 'Autism Quotient (AQ)',
      'eq': 'Empathy Quotient (EQ)',
      'zung': 'Zung Self-Rating Anxiety',
      'scid': 'SCID Screening'
    };
    return names[questionnaireId] || questionnaireId.toUpperCase();
  };

  /**
   * Get questionnaire short name.
   */
  const getQuestionnaireShortName = (questionnaireId: string): string => {
    const names: { [key: string]: string } = {
      'hamilton': 'Hamilton',
      'aq': 'AQ',
      'eq': 'EQ',
      'zung': 'Zung',
      'scid': 'SCID'
    };
    return names[questionnaireId] || questionnaireId.toUpperCase();
  };

  /**
   * Format date to Greek locale.
   */
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('el-GR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  /**
   * Calculate completion time in minutes.
   */
  const calculateCompletionTime = (startedAt?: string, completedAt?: string): string => {
    if (!startedAt || !completedAt) return 'N/A';
    try {
      const start = new Date(startedAt).getTime();
      const end = new Date(completedAt).getTime();
      const diffMinutes = Math.round((end - start) / 60000);
      if (diffMinutes < 1) return '< 1 min';
      return `${diffMinutes} min`;
    } catch {
      return 'N/A';
    }
  };

  /**
   * Get score display from scores object.
   */
  const getScoreDisplay = (scores: { [key: string]: any } | undefined): string => {
    if (!scores || typeof scores !== 'object') return 'N/A';
    if (scores.total_score !== undefined) return scores.total_score.toString();
    if (scores.score !== undefined) return scores.score.toString();
    const values = Object.values(scores).filter(v => typeof v === 'number');
    return values.length > 0 ? values[0].toString() : 'N/A';
  };

  /**
   * Get category badge variant based on questionnaire status.
   */
  const getStatusBadgeVariant = (status: string): string => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'not_started': return 'secondary';
      default: return 'secondary';
    }
  };

  /**
   * Get questionnaire breakdown by type.
   * Shows ALL questionnaire types regardless of status filter.
   */
  const getQuestionnaireBreakdown = (): { [key: string]: number } => {
    if (!questionnaireSummary) return {};
    
    const breakdown: { [key: string]: number } = {};
    
    // Count ALL questionnaires (only exclude not_started)
    questionnaireSummary.questionnaires
      .filter(q => q.status !== 'not_started')
      .forEach((q) => {
        breakdown[q.questionnaire_id] = (breakdown[q.questionnaire_id] || 0) + 1;
      });
    
    return breakdown;
  };

  /**
   * Filter patients based on search query.
   * Searches in: first_name, last_name, amka, mobile_phone, landline_phone
   */
  const getFilteredPatients = (): PatientWithSubmissions[] => {
    if (!searchQuery.trim()) {
      return patientsWithSubmissions;
    }

    const query = searchQuery.toLowerCase().trim();

    return patientsWithSubmissions.filter((patient) => {
      const info = patientInfoMap[patient.patient_id];
      if (!info) return false;

      // Search in multiple fields
      const searchableFields = [
        info.first_name?.toLowerCase() || '',
        info.last_name?.toLowerCase() || '',
        info.amka?.toLowerCase() || '',
        info.mobile_phone?.toLowerCase() || '',
        info.landline_phone?.toLowerCase() || '',
        `${info.first_name} ${info.last_name}`.toLowerCase(), // Full name
      ];

      return searchableFields.some(field => field.includes(query));
    });
  };

  /**
   * Calculate unread questionnaires count for a patient.
   * Unread means the admin hasn't clicked on this specific patient's card yet.
   * All questionnaires are unread until admin clicks on the patient card.
   */
  const calculateUnreadCount = (patient: PatientWithSubmissions): number => {
    // If admin has already clicked on this patient, it's 0 (read)
    if (unreadCounts[patient.patient_id] === 0) {
      return 0;
    }
    
    // If not clicked yet, all questionnaires are unread
    return patient.total_submissions;
  };

  /**
   * Handle patient selection - switch to details view on mobile.
   */
  const handlePatientSelect = (patientId: string) => {
    setSelectedPatientId(patientId);
    if (isMobile) {
      setMobileView('details');
    }
    
    // Mark questionnaires as read when patient is selected
    setUnreadCounts(prev => ({
      ...prev,
      [patientId]: 0
    }));

    // Persist this read action using current total_submissions for that patient
    const patient = patientsWithSubmissions.find(p => p.patient_id === patientId);
    if (patient) {
      const lastSeenMap = loadLastSeenMap();
      lastSeenMap[patientId] = patient.total_submissions;
      saveLastSeenMap(lastSeenMap);
    }
  };

  const questionnaireBreakdown = getQuestionnaireBreakdown();

  // Main render
  return (
    <Container 
      fluid 
      className="analytics-container admin-background"
      style={{
        backgroundImage: 'url(/Login.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <Card className="analytics-card">
        <Card.Header className="analytics-header">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">
              <i className="bi bi-graph-up-arrow me-2" style={{ fontSize: '1.15rem' }}></i>
              Questionnaire Analytics
            </h2>
            <div className="d-flex gap-2">
              <Button
                variant="outline-light"
                onClick={() => {
                  // Refresh all data: patients list, selected patient details, and status counts
                  fetchPatientsWithSubmissions();
                  if (selectedPatientId) {
                    fetchQuestionnaireSummary(selectedPatientId);
                    fetchPatientStatusCounts(selectedPatientId);
                  }
                }}
                className="d-flex align-items-center"
                title="Refresh all data including patient list and questionnaire details"
              >
                <i className="bi bi-arrow-clockwise me-1" style={{ fontSize: '0.8rem' }}></i>
                Refresh
              </Button>
              <Button
                variant="outline-light"
                onClick={() => navigate('/')}
                className="d-flex align-items-center"
              >
                <i className="bi bi-house me-1" style={{ fontSize: '0.8rem' }}></i>
                Home
              </Button>
            </div>
          </div>

          {/* Mobile Navigation Tabs */}
          {isMobile && !loading && !error && patientsWithSubmissions.length > 0 && (
            <div className="mobile-tabs mt-2">
              <Button
                variant={mobileView === 'list' ? 'light' : 'outline-light'}
                onClick={() => setMobileView('list')}
                className="mobile-tab-button"
              >
                <i className="bi bi-people-fill me-1"></i>
                Patients ({patientsWithSubmissions.length})
              </Button>
              <Button
                variant={mobileView === 'details' ? 'light' : 'outline-light'}
                onClick={() => setMobileView('details')}
                className="mobile-tab-button ms-2"
                disabled={!selectedPatientId}
              >
                <i className="bi bi-clipboard-data-fill me-1"></i>
                Analytics
              </Button>
            </div>
          )}
        </Card.Header>

        <Card.Body className="p-2">
          {/* Loading State */}
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <Spinner animation="border" variant="primary" size="sm" />
              <span className="ms-2" style={{ fontSize: '0.85rem' }}>Loading analytics...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="danger">
              <Alert.Heading>Error</Alert.Heading>
              <p>{error}</p>
              <Button variant="outline-danger" onClick={fetchPatientsWithSubmissions}>
                Retry
              </Button>
            </Alert>
          )}

          {/* Empty State */}
          {!loading && !error && patientsWithSubmissions.length === 0 && (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted d-block mb-3"></i>
              <h4>No Submissions Yet</h4>
              <p className="text-muted">
                No patients have submitted questionnaires yet. Assign questionnaires to patients to see analytics here.
              </p>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && patientsWithSubmissions.length > 0 && (
            <Row style={{ flex: 1, overflow: 'hidden', margin: 0 }}>
              {/* Left Panel - Patient List (Desktop: always show, Mobile: only when mobileView === 'list') */}
              <Col 
                lg={3} 
                md={12} 
                style={{ 
                  maxHeight: '65vh', 
                  display: isMobile && mobileView === 'details' ? 'none' : 'flex', 
                  flexDirection: 'column', 
                  padding: '0.25rem' 
                }}
              >
                <Card className="shadow-sm" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Card.Header className="bg-primary text-white" style={{ padding: '0.3rem 0.5rem', flexShrink: 0 }}>
                    <h6 className="mb-0" style={{ fontSize: '0.8rem' }}>
                      <i className="bi bi-people me-1" style={{ fontSize: '0.8rem' }}></i>
                      Patients with Submissions ({patientsWithSubmissions.length})
                    </h6>
                  </Card.Header>
                  
                  {/* Search Input */}
                  <div className="bg-light border-bottom" style={{ padding: '0.25rem 0.4rem', flexShrink: 0 }}>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search by name, SSN, phone..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ paddingLeft: '1.75rem', fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
                      />
                      <i 
                        className="bi bi-search position-absolute text-muted" 
                        style={{ 
                          left: '8px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          fontSize: '0.75rem'
                        }}
                      ></i>
                      {searchQuery && (
                        <button
                          className="btn btn-sm btn-link position-absolute text-muted"
                          onClick={() => setSearchQuery('')}
                          style={{ 
                            right: '5px', 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            padding: '0.1rem 0.25rem',
                            fontSize: '0.75rem'
                          }}
                          title="Clear search"
                        >
                          <i className="bi bi-x-circle"></i>
                        </button>
                      )}
                    </div>
                    {searchQuery && (
                      <small className="text-muted d-block" style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>
                        Found {getFilteredPatients().length} of {patientsWithSubmissions.length} patients
                      </small>
                    )}
                  </div>

                  <Card.Body className="p-0" style={{ flex: 1, overflowY: 'auto' }}>
                    {getFilteredPatients().length > 0 ? (
                      <div className="list-group list-group-flush">
                        {getFilteredPatients().map((patient) => {
                        const info = patientInfoMap[patient.patient_id];
                        const isSelected = selectedPatientId === patient.patient_id;
                        
                        return (
                          <button
                            key={patient.patient_id}
                            className={`list-group-item list-group-item-action ${isSelected ? 'active' : ''}`}
                            onClick={() => handlePatientSelect(patient.patient_id)}
                          >
                            <div className="d-flex w-100 justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <h6 className="mb-1" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                                  {info ? `${info.first_name} ${info.last_name}` : 'Loading...'}
                                </h6>
                                {info && (
                                  <div style={{ fontSize: '0.75rem' }}>
                                    {info.amka && (
                                      <div style={{ marginBottom: '0.15rem' }}>
                                        <i className="bi bi-card-text me-1" style={{ fontSize: '0.7rem' }}></i>
                                        <strong>SSN:</strong> {info.amka}
                                      </div>
                                    )}
                                    {info.mobile_phone && (
                                      <div style={{ marginBottom: '0.15rem' }}>
                                        <i className="bi bi-phone me-1" style={{ fontSize: '0.7rem' }}></i>
                                        <strong>Mobile:</strong> {info.mobile_phone}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>
                                  <i className="bi bi-clock me-1" style={{ fontSize: '0.65rem' }}></i>
                                  Latest: {formatDate(patient.latest_submission)}
                                </div>
                              </div>
                              <div className="text-end ms-2">
                                {/* Unread Count with Status-based Colors */}
                                {(() => {
                                  const unreadCount = unreadCounts[patient.patient_id] || calculateUnreadCount(patient);
                                  const hasUnread = unreadCount > 0;
                                  const statusCounts = patientStatusCounts[patient.patient_id];
                                  
                                  // Determine color based on status
                                  let backgroundColor = '#6c757d'; // Default gray
                                  let titleText = 'All questionnaires read';
                                  
                                  if (hasUnread) {
                                    if (statusCounts) {
                                      // Prioritize completed questionnaires for color (green like completed tab)
                                      if (statusCounts.completed > 0) {
                                        backgroundColor = '#28a745'; // Green for completed (same as completed tab)
                                        titleText = `${unreadCount} unread questionnaire(s) - Some completed`;
                                      } else if (statusCounts.inProgress > 0) {
                                        backgroundColor = '#ffc107'; // Yellow for in_progress
                                        titleText = `${unreadCount} unread questionnaire(s) - Some in progress`;
                                      }
                                    }
                                  }
                                  
                                  return (
                                    <div 
                                      className="d-flex align-items-center justify-content-center"
                                      style={{ 
                                        width: hasUnread ? '42px' : '36px', 
                                        height: '14px', 
                                        borderRadius: '7px',
                                        backgroundColor: backgroundColor,
                                        fontSize: '0.55rem',
                                        fontWeight: '600',
                                        color: 'white',
                                        letterSpacing: '0.3px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      title={titleText}
                                    >
                                      {hasUnread ? unreadCount : '✓'}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    ) : (
                      <div className="text-center py-5">
                        <i className="bi bi-search display-4 text-muted d-block mb-3"></i>
                        <h6 className="text-muted">No patients found</h6>
                        <p className="text-muted small">
                          Try a different search term
                        </p>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => setSearchQuery('')}
                        >
                          <i className="bi bi-x-circle me-2"></i>
                          Clear Search
                        </Button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Right Panel - Patient Analytics (Desktop: always show, Mobile: only when mobileView === 'details') */}
              <Col 
                lg={9} 
                md={12} 
                style={{ 
                  maxHeight: '65vh', 
                  display: isMobile && mobileView === 'list' ? 'none' : 'flex', 
                  flexDirection: 'column', 
                  padding: '0.25rem' 
                }}
              >
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {selectedPatientId ? (
                    <>
                      {/* Filter Buttons - 2 Rows */}
                      <Card className="shadow-sm mb-2" style={{ border: '1px solid #e9ecef', borderRadius: '6px' }}>
                        <Card.Body style={{ padding: '0.5rem 0.75rem' }}>
                          {/* Row 1: 3 Status Filters */}
                          <div className="d-flex gap-2 mb-2" style={{ flexWrap: 'nowrap' }}>
                            <Button
                              size="sm"
                              variant={selectedStatusFilter === 'all' ? 'primary' : 'outline-primary'}
                              onClick={() => handleStatusFilterClick('all')}
                              className="flex-fill analytics-row-btn"
                            >
                              All ({questionnaireSummary?.total_questionnaires || 0})
                            </Button>
                            <Button
                              size="sm"
                              variant={selectedStatusFilter === 'completed' ? 'success' : 'outline-success'}
                              onClick={() => handleStatusFilterClick('completed')}
                              className="flex-fill analytics-row-btn"
                            >
                              Completed ({questionnaireSummary?.completed_questionnaires || 0})
                            </Button>
                            <Button
                              size="sm"
                              variant={selectedStatusFilter === 'in_progress' ? 'warning' : 'outline-warning'}
                              onClick={() => handleStatusFilterClick('in_progress')}
                              className="flex-fill analytics-row-btn"
                            >
                              In Progress ({questionnaireSummary?.in_progress_questionnaires || 0})
                            </Button>
                          </div>

                          {/* Row 2: 5 Questionnaire Type Filters */}
                          {Object.keys(questionnaireBreakdown).length > 0 && (
                            <div className="d-flex gap-2">
                              {Object.entries(questionnaireBreakdown).map(([qId, count]) => (
                                <Button
                                  key={qId}
                                  size="sm"
                                  variant={selectedQuestionnaireTypeFilter === qId ? 'info' : 'outline-secondary'}
                                  onClick={() => handleQuestionnaireTypeFilterClick(qId)}
                                  className="flex-fill analytics-row-btn"
                                >
                                  {getQuestionnaireShortName(qId)} ({count})
                                </Button>
                              ))}
                            </div>
                          )}
                        </Card.Body>
                      </Card>

                      {/* Minimal Questionnaires Table */}
                      <Card className="shadow-sm" style={{ border: '1px solid #e9ecef' }}>
                        <Card.Body className="p-0">
                          {detailLoading ? (
                            <div className="d-flex align-items-center justify-content-center py-3">
                              <div className="text-center">
                                <Spinner animation="border" size="sm" variant="primary" />
                                <span className="ms-2" style={{ fontSize: '0.85rem' }}>Loading...</span>
                              </div>
                            </div>
                          ) : getFilteredQuestionnaires().length > 0 ? (
                            <div className="table-responsive">
                              <Table className="mb-0 minimal-table">
                                <thead>
                                  <tr>
                                    <th>Questionnaire</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-center">Score</th>
                                    <th>Started</th>
                                    <th className="text-center">Duration</th>
                                    <th>Completed</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getFilteredQuestionnaires().map((questionnaire, index) => {
                                    // Create unique key using questionnaire_id + timestamp + index
                                    const uniqueKey = `${questionnaire.questionnaire_id}-${
                                      questionnaire.completed_at || questionnaire.updated_at || questionnaire.created_at || index
                                    }`;
                                    
                                    return (
                                      <tr key={uniqueKey}>
                                        <td>
                                          <div className="fw-semibold" style={{ fontSize: '0.875rem', color: '#2c3e50' }}>
                                            {getQuestionnaireDisplayName(questionnaire.questionnaire_id)}
                                          </div>
                                        </td>
                                        <td className="text-center">
                                          <Badge 
                                            bg={getStatusBadgeVariant(questionnaire.status)} 
                                            className="minimal-badge"
                                          >
                                            {questionnaire.status === 'completed' ? 'Completed' : 
                                             questionnaire.status === 'in_progress' ? 'In Progress' : 
                                             'Not Started'}
                                          </Badge>
                                        </td>
                                        <td className="text-center">
                                          <span className="fw-bold" style={{ fontSize: '0.9rem', color: '#667eea' }}>
                                            {questionnaire.scores ? getScoreDisplay(questionnaire.scores) : '-'}
                                          </span>
                                        </td>
                                        <td style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                                          {questionnaire.started_at ? formatDate(questionnaire.started_at) : '-'}
                                        </td>
                                        <td className="text-center">
                                          <span className="fw-semibold" style={{ fontSize: '0.875rem', color: '#17a2b8' }}>
                                            {calculateCompletionTime(questionnaire.started_at, questionnaire.completed_at)}
                                          </span>
                                        </td>
                                        <td style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                                          {questionnaire.completed_at ? formatDate(questionnaire.completed_at) : 
                                           questionnaire.updated_at ? formatDate(questionnaire.updated_at) : '-'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </div>
                          ) : (
                            <Alert variant="light" className="m-3 text-center" style={{ border: '1px dashed #dee2e6' }}>
                              <i className="bi bi-inbox text-muted" style={{ fontSize: '2rem' }}></i>
                              <p className="mb-0 mt-2 text-muted">No questionnaire data available</p>
                            </Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </>
                  ) : (
                    <Card className="shadow-sm">
                      <Card.Body className="text-center py-5">
                        <i className="bi bi-person-x display-1 text-muted d-block mb-3"></i>
                        <h5>No Patient Selected</h5>
                        <p className="text-muted">
                          Select a patient from the list
                        </p>
                      </Card.Body>
                    </Card>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export { QuestionnaireAnalytics };
